import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, AlertCircle, Sun, Moon } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";

// [Phase 2a.1] Deploy mode is fail-closed: anything other than the literal
// string "public" is treated as admin-test (no demo button, no signup link,
// dark mode only).
const DEPLOY_MODE = process.env.REACT_APP_PGF_DEPLOY_MODE === "public" ? "public" : "admin_test";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const { isDark: themeIsDark, toggleTheme } = useTheme();
  // In admin-test mode, force dark theme regardless of toggle state.
  const isDark = DEPLOY_MODE === "admin_test" ? true : themeIsDark;
  const SHOW_THEME_TOGGLE = DEPLOY_MODE === "public";
  const SHOW_SIGNUP_LINK  = DEPLOY_MODE === "public";

  const [form, setForm] = useState({ email: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try { await login(form.email, form.password); nav("/dashboard"); }
    catch (err) { setError(formatApiErrorDetail(err?.response?.data?.detail) || "Email or password is incorrect."); }
    finally { setLoading(false); }
  };

  const bg    = isDark ? "bg-[#111111]" : "bg-[#F0F0F0]";
  const text  = isDark ? "text-[#F5F5F5]" : "text-[#0A0A0A]";
  const muted = isDark ? "text-[rgba(255,255,255,0.45)]" : "text-[rgba(0,0,0,0.50)]";
  const inputCls = isDark
    ? "bg-[#0A0A0A] border-white/[0.10] focus:border-[#FF6B00] text-[#F5F5F5] placeholder-white/25"
    : "bg-white border-black/[0.10] focus:border-[#FF6B00] text-[#0A0A0A] placeholder-black/25";

  return (
    <div className={`min-h-screen ${bg} flex flex-col items-center justify-center px-4 relative`}>
      {SHOW_THEME_TOGGLE && (
        <button
          data-testid="login-theme-toggle"
          onClick={toggleTheme}
          className={`absolute top-4 right-4 w-9 h-9 rounded-xl flex items-center justify-center border transition-all ${isDark ? "bg-white/[0.06] border-white/[0.10]" : "bg-black/[0.06] border-black/[0.08]"}`}
        >
          {isDark ? <Sun size={15} className="text-[#F5F5F5]" /> : <Moon size={15} className="text-[#0A0A0A]" />}
        </button>
      )}

      <Link to="/" className="flex items-center gap-3 mb-8">
        <div className="bg-[#FF6B00] px-2.5 py-1" style={{ boxShadow: isDark ? "0 0 16px rgba(255,107,0,0.40)" : "none" }}>
          <span className="font-display font-black text-white text-lg uppercase tracking-wider">PGF</span>
        </div>
        <span className={`font-display font-black text-xl uppercase tracking-widest ${text}`}>Pregame Fantasy</span>
      </Link>

      <div className={`w-full max-w-[440px] glass-card rounded-2xl p-7`}>
        <h1 className={`font-display font-black text-3xl uppercase mb-1 ${text}`}>Sign In</h1>
        <p className={`font-body text-sm mb-6 ${muted}`}>Welcome back. Draft night starts here.</p>

        {error && (
          <div className="bg-[#FF4444]/10 border border-[#FF4444]/40 rounded-xl px-4 py-3 flex items-center gap-2 mb-5" data-testid="login-error">
            <AlertCircle size={15} className="text-[#FF4444] shrink-0" />
            <p className="text-[#FF4444] font-body text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={`block font-body text-sm font-medium mb-1.5 ${muted}`}>Email Address</label>
            <input type="email" data-testid="login-email-input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" required
              className={`w-full border rounded-xl px-4 py-3 font-body text-sm outline-none transition-colors ${inputCls}`} />
          </div>
          <div>
            <div className="flex justify-between items-center mb-1.5">
              <label className={`font-body text-sm font-medium ${muted}`}>Password</label>
              <Link to="/forgot-password" className="text-[#FF6B00] font-body text-xs hover:underline">Forgot?</Link>
            </div>
            <div className="relative">
              <input type={showPw ? "text" : "password"} data-testid="login-password-input" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="••••••••" required
                className={`w-full border rounded-xl px-4 py-3 font-body text-sm outline-none transition-colors pr-10 ${inputCls}`} />
              <button type="button" onClick={() => setShowPw(!showPw)} className={`absolute right-3 top-1/2 -translate-y-1/2 ${muted} hover:text-[#F5F5F5] transition-colors`}>
                {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>
          <button type="submit" data-testid="login-submit-btn" disabled={loading}
            className="w-full bg-[#FF6B00] hover:bg-[#FF8533] disabled:opacity-60 text-white font-display font-black uppercase tracking-wide py-3 rounded-xl transition-all active:scale-[0.98] mt-2">
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        {SHOW_SIGNUP_LINK && (
          <p className={`text-center font-body text-sm mt-5 ${muted}`}>
            No account?{" "}<Link to="/signup" className="text-[#FF6B00] hover:underline font-medium">Create one free</Link>
          </p>
        )}
      </div>
    </div>
  );
}
